import { useEffect, useState } from "react";
import { message } from "antd";
import { X } from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";

function isoDay(value) {
  const raw = String(value || "");
  const direct = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (direct) return `${direct[1]}-${direct[2]}-${direct[3]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function clockTime(value) {
  const raw = String(value || "");
  const direct = raw.match(/^(\d{1,2}):(\d{2})/);
  if (direct) return `${direct[1].padStart(2, "0")}:${direct[2]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function durationHours(start, end) {
  const a = clockTime(start).match(/(\d{1,2}):(\d{2})/);
  const b = clockTime(end).match(/(\d{1,2}):(\d{2})/);
  if (!a || !b) return "";
  let minutes = Number(b[1]) * 60 + Number(b[2]) - (Number(a[1]) * 60 + Number(a[2]));
  if (minutes < 0) minutes += 24 * 60;
  return String(Math.round((minutes / 60) * 100) / 100);
}

function hourOn(date, hour) {
  const base = new Date(`${date}T12:00:00`);
  const match = String(hour || "").match(/(\d{1,2}):(\d{2})/);
  if (match) base.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return base;
}

function decimal(value) {
  return String(value ?? "").trim().replace(",", ".");
}

function isPositiveDecimal(value) {
  const parsed = Number(decimal(value));
  return Number.isFinite(parsed) && parsed > 0;
}

export function initialInvoiceDates(commande) {
  let periods;
  if (Array.isArray(commande.dateRanges) && commande.dateRanges.length) {
    periods = commande.dateRanges;
  } else if (Array.isArray(commande.dates) && commande.dates.length) {
    periods = commande.dates.map((date) => ({
      startDate: date?.dateDebut || date?.date || date,
      endDate: date?.dateDebut || date?.date || date,
      startHour: date?.startHour || commande.startHour,
      endHour: date?.endHour || commande.endHour,
      duration: date?.duration,
    }));
  } else if (
    Array.isArray(commande.selectedDates) &&
    commande.selectedDates.length
  ) {
    periods = commande.selectedDates.map((date) => ({
      startDate: date,
      endDate: date,
      startHour: commande.startHour,
      endHour: commande.endHour,
    }));
  } else {
    periods = [{
      startDate: commande.startDate,
      endDate: commande.endDate,
      startHour: commande.startHour,
      endHour: commande.endHour,
    }];
  }
  const rows = [];
  periods.forEach((period) => {
    const start = isoDay(period.startDate || period.date || period);
    if (!start) return;
    const end = isoDay(period.endDate || period.startDate || period.date || period) || start;
    const startDate = new Date(`${start}T12:00:00`);
    const endDate = new Date(`${end}T12:00:00`);
    const span = Math.round((endDate - startDate) / 86400000);
    const count = span >= 0 && span <= 366 ? span + 1 : 1;
    for (let index = 0; index < count; index += 1) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + index);
      const date = isoDay(day);
      const startHour = clockTime(period.startHour || commande.startHour);
      const endHour = clockTime(period.endHour || commande.endHour);
      rows.push({
        date,
        startHour,
        endHour,
        duration: String(period.duration ?? durationHours(startHour, endHour)),
      });
    }
  });
  return rows.length ? rows : [{ date: "", startHour: "", endHour: "", duration: "" }];
}

export default function VacationInvoiceModal({ commande, candidate, client, onClose, onCreated }) {
  const [dates, setDates] = useState(() => initialInvoiceDates(commande));
  const [lines, setLines] = useState(() => {
    const existing = Array.isArray(commande.facturation) ? commande.facturation : [];
    if (existing.length) {
      return existing.map((line) => ({
        description: line.description || "",
        quantity: String(line.quantity ?? ""),
        rate: String(line.rate ?? ""),
      }));
    }
    return [{ description: commande.qualification || "", quantity: "1", rate: String(commande.hourlyRate || "") }];
  });
  const [address, setAddress] = useState(commande.address || client?.address || "");
  const [postCode, setPostCode] = useState(commande.postCode || client?.postCode || "");
  const [town, setTown] = useState(commande.town || client?.town || "");
  const [justificatif, setJustificatif] = useState(commande.justificatif || commande.task || "");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const billingItems = client?.billingItems || [];

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose, saving]);

  const updateDate = (index, field, value) => {
    setDates((current) => current.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const next = { ...row, [field]: value };
      if (field === "startHour" || field === "endHour") next.duration = durationHours(next.startHour, next.endHour);
      return next;
    }));
  };

  const submit = async () => {
    if (saving) return;
    const clientId = client?._id || commande.client?._id || commande.client;
    const candidateId = candidate?._id || candidate;
    if (!clientId || !candidateId || !String(commande.qualification || "").trim()) {
      message.error("Les informations de la commande sont incomplètes (client, candidat ou qualification).");
      return;
    }
    if (!dates.length || dates.some((row) => !row.date || !row.startHour || !row.endHour || !isPositiveDecimal(row.duration))) {
      message.error("Chaque journée doit avoir une date, des horaires et une durée.");
      return;
    }
    if (!lines.length || lines.some((line) => !String(line.description).trim() || !String(line.quantity).trim() || !String(line.rate).trim())) {
      message.error("Ajoutez au moins une ligne de facturation complète.");
      return;
    }
    if (lines.some((line) => !isPositiveDecimal(line.quantity) || !isPositiveDecimal(line.rate))) {
      message.error("Les quantités et les taux HT doivent être strictement positifs.");
      return;
    }
    if (!String(address).trim() || !String(justificatif).trim()) {
      message.error("Renseignez le lieu et le justificatif.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post("bill", {
        type: "Vacation",
        client: clientId,
        candidate: candidateId,
        qualification: String(commande.qualification).trim(),
        order: commande._id,
        address: String(address).trim(),
        postCode: String(postCode).trim(),
        town: String(town).trim(),
        justificatif: String(justificatif).trim(),
        comment: String(comment).trim(),
        dates: dates.map((row) => ({
          dateDebut: new Date(`${row.date}T12:00:00`),
          startHour: hourOn(row.date, row.startHour),
          endHour: hourOn(row.date, row.endHour),
          duration: decimal(row.duration),
        })),
        facturation: lines.map((line) => ({
          description: String(line.description).trim(),
          quantity: decimal(line.quantity),
          rate: decimal(line.rate),
        })),
      });
      message.success("Contrat de vacation et facture créés.");
      onCreated?.(data);
      onClose();
    } catch (error) {
      const detail = apiErrorMessage(error, "La facture n’a pas pu être créée.");
      if (detail) message.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vacation-invoice-title"
        aria-busy={saving}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="vacation-invoice-title" className="text-lg font-bold text-slate-900">Faire la facture</h2>
            <p className="mt-1 text-sm text-slate-500">
              Même flux que la facture à faire d’un contrat de vacation : le contrat et la facture sont créés ensemble pour {candidate.firstname} {candidate.lastname}.
            </p>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} disabled={saving} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"><X size={18} /></button>
        </div>
        <div className="space-y-5">
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Journées</h3>
            <div className="space-y-2">
              {dates.map((row, index) => (
                <div key={index} className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-5">
                  <label className="text-xs text-slate-500">Date<input lang="fr-FR" type="date" value={row.date} onChange={(event) => updateDate(index, "date", event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Début<input lang="fr-FR" type="time" value={row.startHour} onChange={(event) => updateDate(index, "startHour", event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Fin<input lang="fr-FR" type="time" value={row.endHour} onChange={(event) => updateDate(index, "endHour", event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Durée (h)<input value={row.duration} onChange={(event) => updateDate(index, "duration", event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <button type="button" onClick={() => setDates((current) => current.filter((_, rowIndex) => rowIndex !== index))} className="self-end rounded-lg px-2 py-1.5 text-sm text-rose-700">Retirer</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setDates((current) => [...current, { date: "", startHour: commande.startHour || "", endHour: commande.endHour || "", duration: "" }])} className="mt-2 text-sm font-semibold text-blue-700">Ajouter une journée</button>
          </section>
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Facturation</h3>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-4">
                  {billingItems.length ? (
                    <select aria-label="Poste" value={line.description} onChange={(event) => {
                      const description = event.target.value;
                      const found = billingItems.find((item) => item.label === description);
                      setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, description, rate: found?.price != null ? String(found.price) : row.rate } : row));
                    }} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm">
                      <option value="">Poste</option>
                      {line.description && !billingItems.some((item) => item.label === line.description) && <option value={line.description}>{line.description}</option>}
                      {billingItems.map((item, itemIndex) => <option key={item._id || `${item.label}-${itemIndex}`} value={item.label}>{item.label}</option>)}
                    </select>
                  ) : (
                    <input aria-label="Poste" value={line.description} onChange={(event) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, description: event.target.value } : row))} placeholder="Poste" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                  )}
                  <input aria-label="Quantité" inputMode="decimal" value={line.quantity} onChange={(event) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, quantity: event.target.value } : row))} placeholder="Quantité" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                  <input aria-label="Taux HT" inputMode="decimal" value={line.rate} onChange={(event) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, rate: event.target.value } : row))} placeholder="Taux HT" className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                  <button type="button" onClick={() => setLines((current) => current.filter((_, rowIndex) => rowIndex !== index))} className="rounded-lg px-2 py-1.5 text-sm text-rose-700">Retirer</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setLines((current) => [...current, { description: "", quantity: "1", rate: "" }])} className="mt-2 text-sm font-semibold text-blue-700">Ajouter une ligne</button>
          </section>
          <section className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm text-slate-700 sm:col-span-3">Lieu<input value={address} onChange={(event) => setAddress(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-sm text-slate-700">Code postal<input value={postCode} onChange={(event) => setPostCode(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-sm text-slate-700 sm:col-span-2">Ville<input value={town} onChange={(event) => setTown(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-sm text-slate-700 sm:col-span-3">Justificatif / tâches<textarea value={justificatif} onChange={(event) => setJustificatif(event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-sm text-slate-700 sm:col-span-3">Commentaire<textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
          </section>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Fermer</button>
          <button type="button" onClick={submit} disabled={saving} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Création…" : "Créer le contrat et la facture"}</button>
        </div>
      </div>
    </div>
  );
}
